/**
 * Device-class heuristics. Used to gate features that crash low-memory
 * tabs (NER model download on iOS Safari, full-resolution OCR on
 * 12-megapixel phone photos, etc.).
 *
 * No single signal is reliable across vendors:
 *  - `navigator.deviceMemory` exists on Chromium but not Safari/Firefox.
 *  - `navigator.userAgentData` exists on Chromium but not Safari.
 *  - The viewport width is a decent proxy for "small device" but lies
 *    on iPad in desktop-mode and on a phone-in-landscape.
 *
 * We combine signals: if any one of them strongly indicates mobile or
 * low-memory, treat the device as constrained. Errs on the side of
 * "constrained" — false positives on a powerful device just mean
 * NER is opt-in instead of prompted, which is recoverable. False
 * negatives on a low-memory phone mean the tab dies.
 */

const userAgent = (): string =>
  typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';

/** True for phones (iPhone, iPod, Android phones). Deliberately does
 *  NOT match iPads — modern iPad Safari sends a desktop UA by default
 *  and recent iPads have plenty of memory. */
export const isMobileDevice = (): boolean => {
  const ua = userAgent();
  if (/iPhone|iPod/i.test(ua)) return true;
  if (/Android/i.test(ua) && /Mobile/i.test(ua)) return true;
  // Chromium's UA Client Hints — authoritative when present.
  type NavigatorWithUaData = Navigator & { userAgentData?: { mobile?: boolean } };
  const nav = typeof navigator !== 'undefined' ? (navigator as NavigatorWithUaData) : undefined;
  if (nav?.userAgentData?.mobile === true) return true;
  return false;
};

/** True if the runtime reports < 4 GB of device memory. Only Chromium
 *  exposes this; returns false on Safari/Firefox even when the device
 *  is constrained. Use alongside `isMobileDevice` for a fuller check. */
export const isLowMemory = (): boolean => {
  type NavigatorWithMemory = Navigator & { deviceMemory?: number };
  const nav = typeof navigator !== 'undefined' ? (navigator as NavigatorWithMemory) : undefined;
  const mem = nav?.deviceMemory;
  return typeof mem === 'number' && mem < 4;
};

/** True if either the viewport is phone-sized OR the UA is a phone OR
 *  the device reports low memory. The right gate for memory-sensitive
 *  defaults (NER download, max OCR resolution, PDF rasterisation
 *  scale). */
export const isConstrainedDevice = (): boolean => {
  if (isMobileDevice()) return true;
  if (isLowMemory()) return true;
  if (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 900px)').matches
  ) {
    return true;
  }
  return false;
};

/** Pure viewport check — "is this a small screen". Not a memory
 *  proxy; use `isConstrainedDevice` for memory decisions. Useful for
 *  layout-only adjustments. */
export const isSmallViewport = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(max-width: 900px)').matches;
